<template>
    <div class="mac-wrap">

        <!-- ── Header row ── -->
        <div class="mac-header">
            <div class="mac-avatar" @click="pickImage" title="Change photo">
                <img :src="avatarSrc" alt="avatar" class="mac-avatar-img" />
                <div class="mac-avatar-overlay">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                </div>
                <input ref="fileRef" type="file" accept="image/*" class="mac-file-input" @change="onFile" />
            </div>
            <div class="mac-header-info">
                <div class="mac-user-name">{{ formData.full_name || '—' }}</div>
                <div class="mac-user-email">{{ authStore.user?.email || formData.contact_email }}</div>
                <div class="mac-user-badge">
                    <span class="mac-badge-dot"></span>
                    Active
                </div>
            </div>
        </div>

        <!-- ── Card ── -->
        <div class="mac-card">

            <!-- Tabs -->
            <div class="mac-tabs">
                <button
                    v-for="tab in tabs"
                    :key="tab.id"
                    class="mac-tab"
                    :class="{ active: activeTab === tab.id }"
                    @click="requestTabChange(tab.id)"
                    type="button"
                >
                    <svg v-if="tab.id === 'profile'" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                    <svg v-else-if="tab.id === 'address'" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                    <svg v-else-if="tab.id === 'social'" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                    <svg v-else-if="tab.id === 'security'" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                    {{ tab.label }}
                    <span v-if="dirtyTabs.has(tab.id)" class="mac-tab-dirty" title="Unsaved changes"></span>
                </button>
            </div>

            <!-- Loading -->
            <div v-if="loading" class="mac-loading">
                <div class="mac-spinner"></div>
                <span>Loading profile…</span>
            </div>

            <template v-else>
                <!-- ── Tab: Profile ── -->
                <div v-show="activeTab === 'profile'" class="mac-tab-body">
                    <!-- Avatar upload hint -->
                    <div class="mac-upload-hint" @click="pickImage">
                        <div class="mac-upload-thumb">
                            <img :src="avatarSrc" alt="avatar" style="width:100%;height:100%;object-fit:cover;border-radius:50%" />
                        </div>
                        <div>
                            <div class="mac-upload-title">{{ pickedFile ? pickedFile.name : 'Change profile photo' }}</div>
                            <div class="mac-upload-sub">Click to upload · JPG or PNG · max 2 MB</div>
                        </div>
                    </div>

                    <div class="mac-row2">
                        <div class="mac-field">
                            <label class="mac-label">Full Name</label>
                            <input
                                v-model="formData.full_name"
                                type="text"
                                placeholder="Your full name"
                                class="mac-input"
                                :class="{ error: errors.full_name }"
                            />
                            <p v-if="errors.full_name" class="mac-error">{{ errors.full_name }}</p>
                        </div>
                        <div class="mac-field">
                            <label class="mac-label">
                                Contact Email
                                <span class="mac-label-lock">
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                                    locked
                                </span>
                            </label>
                            <input
                                v-model="formData.contact_email"
                                type="email"
                                disabled
                                class="mac-input mac-input-disabled"
                            />
                            <p class="mac-hint">Email cannot be changed here. Contact support if needed.</p>
                        </div>
                    </div>

                    <div class="mac-field">
                        <label class="mac-label">About Me</label>
                        <textarea
                            v-model="formData.about"
                            rows="4"
                            placeholder="Tell others about yourself…"
                            class="mac-input"
                            :class="{ error: errors.about }"
                        ></textarea>
                        <p v-if="errors.about" class="mac-error">{{ errors.about }}</p>
                    </div>
                </div>

                <!-- ── Tab: Address ── -->
                <div v-show="activeTab === 'address'" class="mac-tab-body">
                    <div class="mac-field">
                        <label class="mac-label">Street Address</label>
                        <input v-model="formData.address.street" type="text" placeholder="123 Main Street" class="mac-input" />
                    </div>
                    <div class="mac-row2">
                        <div class="mac-field">
                            <label class="mac-label">City</label>
                            <input v-model="formData.address.city" type="text" placeholder="New York" class="mac-input" />
                        </div>
                        <div class="mac-field">
                            <label class="mac-label">State / Province</label>
                            <input v-model="formData.address.state" type="text" placeholder="NY" class="mac-input" />
                        </div>
                    </div>
                    <div class="mac-row2">
                        <div class="mac-field">
                            <label class="mac-label">Country</label>
                            <select v-model="formData.address.country_id" class="mac-input">
                                <option value="">Select country</option>
                                <option v-for="c in countries" :key="c.id" :value="c.id">{{ c.name }}</option>
                            </select>
                        </div>
                        <div class="mac-field">
                            <label class="mac-label">Postal Code</label>
                            <input v-model="formData.address.postal_code" type="text" placeholder="10001" class="mac-input" />
                        </div>
                    </div>
                </div>

                <!-- ── Tab: Social ── -->
                <div v-show="activeTab === 'social'" class="mac-tab-body">
                    <div
                        v-for="s in socialFields"
                        :key="s.key"
                        class="mac-social-row"
                    >
                        <div class="mac-social-key">
                            <div class="mac-social-icon" v-html="s.iconSvg"></div>
                            <span class="mac-social-label">{{ s.label }}</span>
                        </div>
                        <input
                            v-model="formData[s.key]"
                            type="url"
                            :placeholder="s.placeholder"
                            class="mac-input"
                        />
                    </div>
                </div>

                <!-- ── Tab: Security ── -->
                <div v-show="activeTab === 'security'" class="mac-tab-body">
                    <div class="mac-security-info">
                        <div class="mac-security-icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                        </div>
                        <div>
                            <div class="mac-security-title">Change your password</div>
                            <div class="mac-security-sub">Leave both fields empty to keep your current password</div>
                        </div>
                    </div>

                    <div class="mac-row2">
                        <div class="mac-field">
                            <label class="mac-label">New Password</label>
                            <input
                                v-model="pwdForm.password"
                                type="password"
                                placeholder="At least 8 characters"
                                class="mac-input"
                                :class="{ error: errors.password }"
                                autocomplete="new-password"
                            />
                            <p v-if="errors.password" class="mac-error">{{ errors.password }}</p>
                        </div>
                        <div class="mac-field">
                            <label class="mac-label">Confirm Password</label>
                            <input
                                v-model="pwdForm.password_confirmation"
                                type="password"
                                placeholder="Repeat new password"
                                class="mac-input"
                                :class="{ error: errors.password_confirmation }"
                                autocomplete="new-password"
                            />
                            <p v-if="errors.password_confirmation" class="mac-error">{{ errors.password_confirmation }}</p>
                        </div>
                    </div>
                </div>

                <!-- ── Footer ── -->
                <div class="mac-footer">
                    <div class="mac-footer-left">
                        <span v-if="tabHasChanges" class="mac-unsaved-badge">
                            <span class="mac-unsaved-dot"></span>
                            Unsaved changes
                        </span>
                        <span v-if="successMsg" class="mac-success-badge">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                            {{ successMsg }}
                        </span>
                        <span v-if="failureMsg" class="mac-fail-badge">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                            {{ failureMsg }}
                        </span>
                    </div>
                    <div class="mac-footer-right">
                        <button type="button" class="mac-btn-ghost" :disabled="!tabHasChanges || isSubmitting" @click="resetCurrentTab">
                            Discard
                        </button>
                        <button type="button" class="mac-btn-primary" :disabled="!tabHasChanges || isSubmitting" @click="saveCurrentTab">
                            <svg v-if="!isSubmitting" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                            <svg v-else class="mac-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
                            {{ isSubmitting ? 'Saving…' : 'Save Changes' }}
                        </button>
                    </div>
                </div>
            </template>
        </div>

        <!-- ── Unsaved changes dialog ── -->
        <Teleport to="body">
            <div v-if="pendingTab" class="mac-dialog-backdrop" @click.self="pendingTab = null">
                <div class="mac-dialog">
                    <div class="mac-dialog-icon">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    </div>
                    <div class="mac-dialog-title">Unsaved changes</div>
                    <div class="mac-dialog-body">You have unsaved changes on this tab. If you leave now, your changes will be lost.</div>
                    <div class="mac-dialog-actions">
                        <button type="button" class="mac-btn-ghost" @click="pendingTab = null">Stay here</button>
                        <button type="button" class="mac-btn-danger" @click="confirmTabChange">Leave without saving</button>
                    </div>
                </div>
            </div>
        </Teleport>

    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useAuthStore } from '@/stores'
import onboardingApi from '@/api/onboarding'
import appApi from '@/api/app'
import { updateProfile } from '@/api/me'

const authStore = useAuthStore()

// ── tabs ──────────────────────────────────────────────────────────────────────
const tabs = [
    { id: 'profile',  label: 'Profile'  },
    { id: 'address',  label: 'Address'  },
    { id: 'social',   label: 'Social'   },
    { id: 'security', label: 'Security' },
]
const activeTab  = ref('profile')
const pendingTab = ref(null)   // tab id waiting for confirmation
const dirtyTabs  = ref(new Set())

// ── avatar ────────────────────────────────────────────────────────────────────
const fileRef    = ref(null)
const previewUrl = ref(null)
const pickedFile = ref(null)

const avatarSrc = computed(() => {
    if (previewUrl.value) return previewUrl.value
    const p = authStore.user?.profile_image
    if (p) return p.startsWith('http') ? p : '/storage/' + p
    const n = encodeURIComponent(formData.full_name || formData.contact_email || 'U')
    return 'https://ui-avatars.com/api/?name=' + n + '&background=10b981&color=fff&size=160'
})

const pickImage = () => fileRef.value?.click()
const onFile = (e) => {
    const f = e.target.files?.[0]
    if (!f) return
    pickedFile.value = f
    previewUrl.value = URL.createObjectURL(f)
    dirtyTabs.value.add('profile')
    dirtyTabs.value = new Set(dirtyTabs.value)
}

// ── state ─────────────────────────────────────────────────────────────────────
const loading      = ref(false)
const isSubmitting = ref(false)
const successMsg   = ref('')
const failureMsg   = ref('')
const errors       = reactive({})
const countries    = ref([])

const formData = reactive({
    full_name:     '',
    contact_email: '',
    about:         '',
    address: { street: '', city: '', state: '', country_id: '', postal_code: '' },
    linkedin_url:  '',
    instagram_url: '',
    twitter_url:   '',
    facebook_url:  '',
    youtube_url:   '',
    github_url:    '',
    website_url:   '',
})
const pwdForm = reactive({ password: '', password_confirmation: '' })

// Snapshots per tab for dirty detection
const snapshots = reactive({
    profile:  null,
    address:  null,
    social:   null,
    security: null,
})

// ── social fields ─────────────────────────────────────────────────────────────
const socialFields = [
    { key: 'linkedin_url',  label: 'LinkedIn',  placeholder: 'https://linkedin.com/in/username',
      iconSvg: '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6zM2 9h4v12H2z"/><circle cx="4" cy="4" r="2"/></svg>' },
    { key: 'instagram_url', label: 'Instagram', placeholder: 'https://instagram.com/username',
      iconSvg: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="5"/><circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" stroke="none"/></svg>' },
    { key: 'twitter_url',   label: 'Twitter / X', placeholder: 'https://twitter.com/username',
      iconSvg: '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>' },
    { key: 'facebook_url',  label: 'Facebook',  placeholder: 'https://facebook.com/username',
      iconSvg: '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>' },
    { key: 'youtube_url',   label: 'YouTube',   placeholder: 'https://youtube.com/@username',
      iconSvg: '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 0 0-1.95 1.96A29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58A2.78 2.78 0 0 0 3.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.95A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58zM9.75 15.02V8.98L15.5 12l-5.75 3.02z"/></svg>' },
    { key: 'github_url',    label: 'GitHub',    placeholder: 'https://github.com/username',
      iconSvg: '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"/></svg>' },
    { key: 'website_url',   label: 'Website',   placeholder: 'https://yourwebsite.com',
      iconSvg: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>' },
]

// ── dirty detection ───────────────────────────────────────────────────────────
const getTabSnapshot = (tabId) => {
    if (tabId === 'profile')  return JSON.stringify({ full_name: formData.full_name, about: formData.about, hasFile: !!pickedFile.value })
    if (tabId === 'address')  return JSON.stringify(formData.address)
    if (tabId === 'social')   return JSON.stringify({ linkedin_url: formData.linkedin_url, instagram_url: formData.instagram_url, twitter_url: formData.twitter_url, facebook_url: formData.facebook_url, youtube_url: formData.youtube_url, github_url: formData.github_url, website_url: formData.website_url })
    if (tabId === 'security') return JSON.stringify(pwdForm)
    return ''
}

const tabHasChanges = computed(() => {
    if (!snapshots[activeTab.value]) return activeTab.value === 'profile' && !!pickedFile.value
    return getTabSnapshot(activeTab.value) !== snapshots[activeTab.value]
})

const markTabClean = (tabId) => {
    snapshots[tabId] = getTabSnapshot(tabId)
    dirtyTabs.value.delete(tabId)
    dirtyTabs.value = new Set(dirtyTabs.value)
}

// ── tab navigation ────────────────────────────────────────────────────────────
const requestTabChange = (tabId) => {
    if (tabId === activeTab.value) return
    if (tabHasChanges.value) {
        pendingTab.value = tabId
        return
    }
    activeTab.value = tabId
    clearMessages()
}

const confirmTabChange = () => {
    resetCurrentTab()
    activeTab.value = pendingTab.value
    pendingTab.value = null
    clearMessages()
}

// ── data fetch ────────────────────────────────────────────────────────────────
const fetchData = async () => {
    loading.value = true
    try {
        const [countriesRes, profileRes] = await Promise.all([
            appApi.forms.countries().catch(() => ({ data: [] })),
            onboardingApi.get().catch(() => ({ data: null })),
        ])
        countries.value = countriesRes.data || []

        const profile = profileRes.data?.profile
        if (profile) {
            formData.full_name     = profile.full_name     || ''
            formData.contact_email = profile.contact_email || authStore.user?.email || ''
            formData.about         = profile.about         || ''
            formData.linkedin_url  = profile.linkedin_url  || ''
            formData.instagram_url = profile.instagram_url || ''
            formData.twitter_url   = profile.twitter_url   || ''
            formData.facebook_url  = profile.facebook_url  || ''
            formData.youtube_url   = profile.youtube_url   || ''
            formData.github_url    = profile.github_url    || ''
            formData.website_url   = profile.website_url   || ''
            if (profile.address) {
                Object.assign(formData.address, {
                    street:      profile.address.street      || '',
                    city:        profile.address.city        || '',
                    state:       profile.address.state       || '',
                    country_id:  profile.address.country_id  || '',
                    postal_code: profile.address.postal_code || '',
                })
            }
        } else {
            // fallback from auth store
            formData.full_name     = authStore.user?.name  || ''
            formData.contact_email = authStore.user?.email || ''
        }

        // Set snapshots after data loaded
        tabs.forEach(t => markTabClean(t.id))
    } finally {
        loading.value = false
    }
}

// ── save ──────────────────────────────────────────────────────────────────────
const clearMessages = () => { successMsg.value = ''; failureMsg.value = '' }

const saveCurrentTab = async () => {
    isSubmitting.value = true
    clearMessages()
    Object.keys(errors).forEach(k => delete errors[k])

    try {
        const tab = activeTab.value

        if (tab === 'profile') {
            // Avatar / name via /api/me/profile (if avatar changed)
            if (pickedFile.value) {
                const fd = new FormData()
                fd.append('name',  formData.full_name || '')
                fd.append('email', authStore.user?.email || formData.contact_email || '')
                fd.append('profile_image', pickedFile.value)
                const r = await updateProfile(fd)
                if (r.data?.user) authStore.setUser(r.data.user)
            }
            // Extended profile via onboardingApi
            const fd2 = new FormData()
            fd2.append('full_name',     formData.full_name     || '')
            fd2.append('contact_email', formData.contact_email || '')
            fd2.append('about',         formData.about         || '')
            const r2 = await onboardingApi.set(fd2)
            if (r2.data?.profile) authStore.setUser({ ...authStore.user, ...r2.data.profile })
            pickedFile.value = null
            previewUrl.value = null
        }

        else if (tab === 'address') {
            const fd = new FormData()
            fd.append('full_name',     formData.full_name     || '')
            fd.append('contact_email', formData.contact_email || '')
            Object.entries(formData.address).forEach(([k, v]) => fd.append(k, v || ''))
            await onboardingApi.set(fd)
        }

        else if (tab === 'social') {
            const fd = new FormData()
            fd.append('full_name',     formData.full_name     || '')
            fd.append('contact_email', formData.contact_email || '')
            socialFields.forEach(s => fd.append(s.key, formData[s.key] || ''))
            await onboardingApi.set(fd)
        }

        else if (tab === 'security') {
            if (!pwdForm.password) {
                failureMsg.value = 'Enter a new password first.'
                return
            }
            const fd = new FormData()
            fd.append('name',  formData.full_name || '')
            fd.append('email', authStore.user?.email || formData.contact_email || '')
            fd.append('password', pwdForm.password)
            fd.append('password_confirmation', pwdForm.password_confirmation)
            const r = await updateProfile(fd)
            if (r.data?.user) authStore.setUser(r.data.user)
            pwdForm.password = ''
            pwdForm.password_confirmation = ''
        }

        markTabClean(tab)
        successMsg.value = 'Saved!'
        setTimeout(() => { successMsg.value = '' }, 3000)

    } catch (err) {
        if (err.response?.status === 422) {
            const raw = err.response.data?.errors || {}
            for (const k in raw) errors[k] = Array.isArray(raw[k]) ? raw[k][0] : raw[k]
            failureMsg.value = 'Please fix the errors below.'
        } else {
            failureMsg.value = 'Something went wrong. Please try again.'
        }
    } finally {
        isSubmitting.value = false
    }
}

// ── reset ─────────────────────────────────────────────────────────────────────
const resetCurrentTab = () => {
    const tab = activeTab.value
    if (tab === 'profile') {
        const snap = snapshots.profile ? JSON.parse(snapshots.profile) : {}
        formData.full_name = snap.full_name || ''
        formData.about     = snap.about     || ''
        pickedFile.value   = null
        previewUrl.value   = null
    } else if (tab === 'address') {
        const snap = snapshots.address ? JSON.parse(snapshots.address) : {}
        Object.assign(formData.address, { street: '', city: '', state: '', country_id: '', postal_code: '', ...snap })
    } else if (tab === 'social') {
        const snap = snapshots.social ? JSON.parse(snapshots.social) : {}
        socialFields.forEach(s => { formData[s.key] = snap[s.key] || '' })
    } else if (tab === 'security') {
        pwdForm.password = ''
        pwdForm.password_confirmation = ''
    }
    Object.keys(errors).forEach(k => delete errors[k])
    clearMessages()
}

// ── lifecycle ─────────────────────────────────────────────────────────────────
onMounted(fetchData)
</script>

<style scoped>
/* ── Layout ── */
.mac-wrap { max-width: 680px; margin: 0 auto; padding: 24px 16px 48px; font-family: 'DM Sans', system-ui, -apple-system, sans-serif; }

/* ── Header ── */
.mac-header { display: flex; align-items: center; gap: 16px; margin-bottom: 20px; }
.mac-avatar { width: 68px; height: 68px; border-radius: 50%; flex-shrink: 0; cursor: pointer; position: relative; overflow: hidden; border: 2.5px solid #d1fae5; box-shadow: 0 0 0 3px #ecfdf5; }
.mac-avatar-img { width: 100%; height: 100%; object-fit: cover; display: block; }
.mac-avatar-overlay { position: absolute; inset: 0; background: rgba(0,0,0,.45); display: flex; align-items: center; justify-content: center; opacity: 0; transition: opacity .15s; }
.mac-avatar:hover .mac-avatar-overlay { opacity: 1; }
.mac-file-input { display: none; }
.mac-user-name  { font-size: 18px; font-weight: 700; color: var(--tw-text, #111827); line-height: 1.2; }
.mac-user-email { font-size: 13px; color: var(--tw-text2, #6B7280); margin-top: 1px; }
.mac-user-badge { display: inline-flex; align-items: center; gap: 5px; margin-top: 5px; font-size: 11px; font-weight: 600; letter-spacing: .04em; color: #059669; background: #ecfdf5; border: 1px solid #a7f3d0; border-radius: 20px; padding: 2px 8px; }
.mac-badge-dot  { width: 6px; height: 6px; border-radius: 50%; background: #10b981; }

/* ── Card ── */
.mac-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 16px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,.06); }

/* ── Tabs ── */
.mac-tabs { display: flex; border-bottom: 1px solid #e5e7eb; padding: 0 4px; background: #f9fafb; }
.mac-tab  { position: relative; display: flex; align-items: center; gap: 6px; padding: 13px 16px; font-size: 13px; font-weight: 500; color: #6b7280; border: none; background: none; cursor: pointer; border-bottom: 2px solid transparent; margin-bottom: -1px; transition: color .15s, border-color .15s; font-family: inherit; white-space: nowrap; }
.mac-tab:hover { color: #374151; }
.mac-tab.active { color: #10b981; border-bottom-color: #10b981; font-weight: 600; background: transparent; }
.mac-tab svg { opacity: .7; }
.mac-tab.active svg { opacity: 1; }
.mac-tab-dirty { position: absolute; top: 10px; right: 8px; width: 6px; height: 6px; border-radius: 50%; background: #f59e0b; }

/* ── Loading ── */
.mac-loading { padding: 48px; display: flex; align-items: center; justify-content: center; gap: 10px; font-size: 14px; color: #9ca3af; }
.mac-spinner { width: 20px; height: 20px; border: 2px solid #e5e7eb; border-top-color: #10b981; border-radius: 50%; animation: spin .7s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
@keyframes spin2 { to { transform: rotate(360deg); } }
.mac-spin { animation: spin2 .7s linear infinite; }

/* ── Tab body ── */
.mac-tab-body { padding: 24px; display: flex; flex-direction: column; gap: 16px; }

/* ── Upload hint ── */
.mac-upload-hint { display: flex; align-items: center; gap: 12px; padding: 14px 16px; border: 1.5px dashed #d1d5db; border-radius: 10px; cursor: pointer; transition: border-color .15s, background .15s; }
.mac-upload-hint:hover { border-color: #10b981; background: #f0fdf9; }
.mac-upload-thumb { width: 44px; height: 44px; border-radius: 50%; overflow: hidden; flex-shrink: 0; background: #d1fae5; }
.mac-upload-title { font-size: 13px; font-weight: 600; color: #111827; }
.mac-upload-sub   { font-size: 11px; color: #9ca3af; margin-top: 2px; }

/* ── Fields ── */
.mac-row2  { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.mac-field { display: flex; flex-direction: column; gap: 5px; }
.mac-label { font-size: 11px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: #6b7280; display: flex; align-items: center; gap: 6px; }
.mac-label-lock { display: inline-flex; align-items: center; gap: 3px; font-size: 10px; font-weight: 600; color: #9ca3af; letter-spacing: .04em; }
.mac-input { width: 100%; padding: 9px 12px; border: 1px solid #d1d5db; border-radius: 8px; background: #fff; color: #111827; font-family: inherit; font-size: 14px; outline: none; transition: border-color .15s; }
.mac-input:focus { border-color: #10b981; box-shadow: 0 0 0 3px rgba(16,185,129,.08); }
.mac-input.error  { border-color: #ef4444; }
.mac-input.error:focus { box-shadow: 0 0 0 3px rgba(239,68,68,.08); }
.mac-input-disabled { background: #f9fafb; color: #9ca3af; cursor: not-allowed; }
textarea.mac-input { resize: none; }
select.mac-input  { cursor: pointer; }
.mac-error { font-size: 11px; color: #ef4444; }
.mac-hint  { font-size: 11px; color: #9ca3af; }

/* ── Social rows ── */
.mac-social-row  { display: grid; grid-template-columns: 120px 1fr; align-items: center; gap: 10px; }
.mac-social-key  { display: flex; align-items: center; gap: 8px; }
.mac-social-icon { width: 32px; height: 32px; border-radius: 7px; background: #f3f4f6; border: 1px solid #e5e7eb; display: flex; align-items: center; justify-content: center; flex-shrink: 0; color: #6b7280; }
.mac-social-label{ font-size: 12px; font-weight: 600; color: #374151; }

/* ── Security ── */
.mac-security-info  { display: flex; align-items: flex-start; gap: 12px; padding: 14px 16px; background: #ecfdf5; border: 1px solid #a7f3d0; border-radius: 10px; }
.mac-security-icon  { width: 38px; height: 38px; border-radius: 8px; background: #fff; display: flex; align-items: center; justify-content: center; flex-shrink: 0; color: #10b981; border: 1px solid #a7f3d0; }
.mac-security-title { font-size: 13px; font-weight: 600; color: #065f46; }
.mac-security-sub   { font-size: 12px; color: #059669; margin-top: 2px; }

/* ── Footer ── */
.mac-footer       { padding: 14px 24px; border-top: 1px solid #f3f4f6; background: #f9fafb; display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.mac-footer-left  { display: flex; align-items: center; gap: 10px; min-height: 28px; }
.mac-footer-right { display: flex; align-items: center; gap: 8px; }
.mac-unsaved-badge{ display: inline-flex; align-items: center; gap: 5px; font-size: 12px; font-weight: 500; color: #92400e; background: #fffbeb; border: 1px solid #fcd34d; border-radius: 20px; padding: 3px 10px; }
.mac-unsaved-dot  { width: 6px; height: 6px; border-radius: 50%; background: #f59e0b; flex-shrink: 0; }
.mac-success-badge{ display: inline-flex; align-items: center; gap: 5px; font-size: 12px; font-weight: 500; color: #065f46; background: #ecfdf5; border: 1px solid #a7f3d0; border-radius: 20px; padding: 3px 10px; }
.mac-fail-badge   { display: inline-flex; align-items: center; gap: 5px; font-size: 12px; font-weight: 500; color: #991b1b; background: #fef2f2; border: 1px solid #fca5a5; border-radius: 20px; padding: 3px 10px; }

/* ── Buttons ── */
.mac-btn-primary { display: inline-flex; align-items: center; gap: 6px; padding: 8px 18px; background: #10b981; color: #fff; border: none; border-radius: 8px; font-family: inherit; font-size: 13px; font-weight: 600; cursor: pointer; transition: background .15s; }
.mac-btn-primary:hover:not(:disabled) { background: #059669; }
.mac-btn-primary:disabled { opacity: .45; cursor: not-allowed; }
.mac-btn-ghost   { display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; background: none; color: #6b7280; border: 1px solid #d1d5db; border-radius: 8px; font-family: inherit; font-size: 13px; font-weight: 500; cursor: pointer; transition: border-color .15s, color .15s; }
.mac-btn-ghost:hover:not(:disabled) { border-color: #9ca3af; color: #374151; }
.mac-btn-ghost:disabled { opacity: .4; cursor: not-allowed; }
.mac-btn-danger  { display: inline-flex; align-items: center; gap: 6px; padding: 8px 18px; background: #ef4444; color: #fff; border: none; border-radius: 8px; font-family: inherit; font-size: 13px; font-weight: 600; cursor: pointer; transition: background .15s; }
.mac-btn-danger:hover { background: #dc2626; }

/* ── Dialog ── */
.mac-dialog-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,.35); display: flex; align-items: center; justify-content: center; z-index: 9999; backdrop-filter: blur(2px); }
.mac-dialog { background: #fff; border-radius: 16px; padding: 28px 28px 24px; max-width: 380px; width: calc(100% - 32px); box-shadow: 0 20px 60px rgba(0,0,0,.2); text-align: center; }
.mac-dialog-icon  { width: 48px; height: 48px; border-radius: 50%; background: #fffbeb; border: 1.5px solid #fcd34d; display: flex; align-items: center; justify-content: center; margin: 0 auto 14px; color: #d97706; }
.mac-dialog-title { font-size: 16px; font-weight: 700; color: #111827; margin-bottom: 8px; }
.mac-dialog-body  { font-size: 14px; color: #6b7280; line-height: 1.5; margin-bottom: 20px; }
.mac-dialog-actions { display: flex; gap: 8px; justify-content: center; }

/* ── Responsive ── */
@media (max-width: 520px) {
    .mac-row2 { grid-template-columns: 1fr; }
    .mac-social-row { grid-template-columns: 1fr; }
    .mac-social-key { display: none; }
    .mac-tabs { overflow-x: auto; scrollbar-width: none; }
    .mac-tabs::-webkit-scrollbar { display: none; }
    .mac-wrap { padding: 16px 0 48px; }
}
</style>
